import axios from 'axios';
import { getEntraApiAccessToken } from '@/services/auth-utils.js';

function api() {
  return axios.create({
    baseURL: process.env.VUE_APP_ENTRA_API_BASE_URL || 'http://localhost:3000',
    timeout: 30000
  });
}

function normaliseError(error) {
  const responseError = error && error.response && error.response.data && error.response.data.error;
  return {
    code: responseError ? responseError.code : (error && error.code ? error.code : 'UNKNOWN_API_ERROR'),
    message: responseError ? responseError.message : (error && error.message || 'An unexpected error occurred.'),
    correlationId: responseError ? responseError.correlationId : null,
    status: error && error.response ? error.response.status : (error && error.status ? error.status : null),
    details: responseError ? responseError.details : null
  };
}

async function getHeaders() {
  const token = await getEntraApiAccessToken();
  return {
    Authorization: 'Bearer ' + token,
    'Content-Type': 'application/json'
  };
}

// ==================== Portfolio ====================

export async function getEntraPortfolioSummary(params) {
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/portfolio/summary', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraPortfolioTenants(params) {
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/portfolio/tenants', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraPortfolioFindings(params) {
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/portfolio/findings', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraPortfolioMetrics(params) {
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/portfolio/metrics', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Tenant Overview ====================

export async function getEntraTenantSummary(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/summary', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraTenantCapabilities(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/capabilities', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraTenantCollectionStatus(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/collection-status', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function requestEntraTenantCollection(tenantId, collectionType) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().post('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/collections', { jobType: collectionType }, { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Admin ====================

export async function registerEntraTenant(data) {
  try {
    const headers = await getHeaders();
    const response = await api().post('/api/v1/admin/entra/tenants', data, { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Users ====================

export async function getEntraUsers(tenantId, params) {
  if (!tenantId) throw new Error('tenantId is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/users', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraUserDetails(tenantId, userId) {
  if (!tenantId || !userId) throw new Error('tenantId and userId are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/users/' + encodeURIComponent(userId), { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraUserLicences(tenantId, userId) {
  if (!tenantId || !userId) throw new Error('tenantId and userId are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/users/' + encodeURIComponent(userId) + '/licences', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraUserActivity(tenantId, userId) {
  if (!tenantId || !userId) throw new Error('tenantId and userId are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/users/' + encodeURIComponent(userId) + '/activity', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraActivity(tenantId, params) {
  if (!tenantId) throw new Error('tenantId is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/activity', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraUserAuthentication(tenantId, userId) {
  if (!tenantId || !userId) throw new Error('tenantId and userId are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/users/' + encodeURIComponent(userId) + '/authentication', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraUserRoles(tenantId, userId) {
  if (!tenantId || !userId) throw new Error('tenantId and userId are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/users/' + encodeURIComponent(userId) + '/roles', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraUserGroups(tenantId, userId) {
  if (!tenantId || !userId) throw new Error('tenantId and userId are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/users/' + encodeURIComponent(userId) + '/groups', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraUserFindings(tenantId, userId) {
  if (!tenantId || !userId) throw new Error('tenantId and userId are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/users/' + encodeURIComponent(userId) + '/findings', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Licences ====================

export async function getEntraLicenceSummary(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/licences/summary', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraSkus(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/licences/skus', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraSkuDetails(tenantId, skuId) {
  if (!tenantId || !skuId) throw new Error('tenantId and skuId are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/licences/skus/' + encodeURIComponent(skuId), { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraSkuUsers(tenantId, skuId, params) {
  if (!tenantId || !skuId) throw new Error('tenantId and skuId are required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/licences/skus/' + encodeURIComponent(skuId) + '/users', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraLicenceOpportunities(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/licences/opportunities', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraLicenceHistory(tenantId, params) {
  if (!tenantId) throw new Error('tenantId is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/licences/history', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraLicenceForecast(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/licences/forecast', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Groups ====================

export async function getEntraGroups(tenantId, params) {
  if (!tenantId) throw new Error('tenantId is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/groups', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraGroupDetails(tenantId, groupId) {
  if (!tenantId || !groupId) throw new Error('tenantId and groupId are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/groups/' + encodeURIComponent(groupId), { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraGroupOwners(tenantId, groupId) {
  if (!tenantId || !groupId) throw new Error('tenantId and groupId are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/groups/' + encodeURIComponent(groupId) + '/owners', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraGroupMembers(tenantId, groupId, params) {
  if (!tenantId || !groupId) throw new Error('tenantId and groupId are required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/groups/' + encodeURIComponent(groupId) + '/members', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraGroupFindings(tenantId, groupId) {
  if (!tenantId || !groupId) throw new Error('tenantId and groupId are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/groups/' + encodeURIComponent(groupId) + '/findings', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Security ====================

export async function getEntraSecuritySummary(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/security/summary', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraAuthenticationPosture(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/security/authentication', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraPrivilegedAccess(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/security/privileged-access', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraRiskyUsers(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/security/risky-users', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraConditionalAccess(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/security/conditional-access', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraHealthScore(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/security/score', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Applications ====================

export async function getEntraApplications(tenantId, params) {
  if (!tenantId) throw new Error('tenantId is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/applications', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraApplicationDetails(tenantId, applicationId) {
  if (!tenantId || !applicationId) throw new Error('tenantId and applicationId are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/applications/' + encodeURIComponent(applicationId), { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraEnterpriseApplications(tenantId, params) {
  if (!tenantId) throw new Error('tenantId is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/enterprise-applications', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraEnterpriseAppDetails(tenantId, servicePrincipalId) {
  if (!tenantId || !servicePrincipalId) throw new Error('tenantId and servicePrincipalId are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/enterprise-applications/' + encodeURIComponent(servicePrincipalId), { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraApplicationCredentials(tenantId, params) {
  if (!tenantId) throw new Error('tenantId is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/application-credentials', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraApplicationsSummary(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/applications/summary', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Conditional Access ====================

export async function getEntraConditionalAccessPolicies(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/conditional-access/policies', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraNamedLocations(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/conditional-access/named-locations', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function exportConditionalAccessDocument(tenantId, options) {
  if (!tenantId) throw new Error('tenantId is required');
  var opts = options || {};
  var format = opts.format || 'pdf';
  try {
    var headers = await getHeaders();
    var response = await api().post(
      '/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/conditional-access/export',
      {
        format: format,
        policyIds: opts.policyIds || undefined,
        locationIds: opts.locationIds || undefined,
        includeLocations: opts.includeLocations !== false,
        sections: {
          cover: opts.sections ? opts.sections.cover !== false : true,
          summary: opts.sections ? opts.sections.summary !== false : true,
          details: opts.sections ? opts.sections.details !== false : true,
          aiSummaries: opts.sections ? opts.sections.aiSummaries !== false : true,
          namedLocations: opts.sections ? opts.sections.namedLocations !== false : true
        }
      },
      { headers: headers, responseType: 'blob', timeout: 120000 }
    );
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getCaPolicySummary(tenantId, policyId) {
  if (!tenantId || !policyId) throw new Error('tenantId and policyId are required');
  try {
    var headers = await getHeaders();
    var response = await api().get(
      '/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/conditional-access/policies/' + encodeURIComponent(policyId) + '/summary',
      { headers: headers }
    );
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function generateCaPolicySummary(tenantId, policyId) {
  if (!tenantId || !policyId) throw new Error('tenantId and policyId are required');
  try {
    var headers = await getHeaders();
    var response = await api().post(
      '/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/conditional-access/policies/' + encodeURIComponent(policyId) + '/summary',
      {},
      { headers: headers, timeout: 120000 }
    );
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getAllCaPolicySummaries(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    var headers = await getHeaders();
    var response = await api().get(
      '/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/conditional-access/summaries',
      { headers: headers }
    );
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Authentication Methods ====================

export async function getEntraAuthenticationMethods(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/authentication-methods', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Sign-in Logs ====================

export async function getEntraSignInLogs(tenantId, params) {
  if (!tenantId) throw new Error('tenantId is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/sign-in-logs', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Intune / Device Management ====================

export async function getEntraIntune(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/intune', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Teams ====================

export async function getEntraTeams(tenantId) {
  if (!tenantId) throw new Error('tenantId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/teams', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Findings / Recommendations ====================

export async function getEntraFindings(tenantId, params) {
  if (!tenantId) throw new Error('tenantId is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/findings', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraFindingDetails(tenantId, findingId) {
  if (!tenantId || !findingId) throw new Error('tenantId and findingId are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/findings/' + encodeURIComponent(findingId), { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function updateEntraFinding(tenantId, findingId, payload) {
  if (!tenantId || !findingId) throw new Error('tenantId and findingId are required');
  try {
    const headers = await getHeaders();
    const response = await api().patch('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/findings/' + encodeURIComponent(findingId), payload, { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function addEntraFindingNote(tenantId, findingId, note) {
  if (!tenantId || !findingId) throw new Error('tenantId and findingId are required');
  try {
    const headers = await getHeaders();
    const response = await api().post('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/findings/' + encodeURIComponent(findingId) + '/notes', { comment: note }, { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function excludeEntraFinding(tenantId, findingId, exclusion) {
  if (!tenantId || !findingId) throw new Error('tenantId and findingId are required');
  try {
    const headers = await getHeaders();
    const response = await api().post('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/findings/' + encodeURIComponent(findingId) + '/exclude', exclusion, { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function reevaluateEntraFinding(tenantId, findingId) {
  if (!tenantId || !findingId) throw new Error('tenantId and findingId are required');
  try {
    const headers = await getHeaders();
    const response = await api().post('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/findings/' + encodeURIComponent(findingId) + '/re-evaluate', {}, { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Exports ====================

export async function requestEntraExport(tenantId, params) {
  if (!tenantId) throw new Error('tenantId is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().post('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/exports', params, { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getEntraExportStatus(tenantId, exportId) {
  if (!tenantId || !exportId) throw new Error('tenantId and exportId are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/entra/tenants/' + encodeURIComponent(tenantId) + '/exports/' + encodeURIComponent(exportId), { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Policy AI Explanations ====================

export async function getPolicyExplanation(policyKey) {
  if (!policyKey) throw new Error('policyKey is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/policy-explanations/' + encodeURIComponent(policyKey), { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function savePolicyExplanation(policyKey, payload) {
  if (!policyKey || !payload) throw new Error('policyKey and payload are required');
  try {
    const headers = await getHeaders();
    const response = await api().post('/api/v1/policy-explanations/' + encodeURIComponent(policyKey), payload, { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getPolicyExplanationStatus() {
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/policy-explanations/status', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}
